<template lang="pug">
.scan-app
    //- Hinweis fuer nicht unterstuetzte iOS-Versionen (< 15.1)
    .ios-warning(v-if="iosUnsupported")
        i.el-icon-warning
        span {{ $t('Diese App benötigt iOS 15.1 oder neuer. Bitte aktualisiere dein Gerät für die volle Funktion.') }}
    //- Login
    .scan-login(v-if="!isAuthenticated")
        .login-card
            .login-logo
                img(:src="logo" alt="CityOffer")
                h2 CityOffer App
            .login-fields
                el-input(
                    v-model="credentials.email"
                    :placeholder="$t('E-Mail')"
                    prefix-icon="el-icon-fa-envelope fas"
                    type="email"
                    size="medium"
                    autocomplete="email"
                )
                el-input(
                    ref="passwordInput"
                    v-model="credentials.password"
                    :placeholder="$t('Passwort')"
                    prefix-icon="el-icon-fa-lock fas"
                    type="password"
                    size="medium"
                    autocomplete="current-password"
                    @keyup.enter.native="login"
                )
            el-alert.login-error(
                v-if="loginError"
                :title="loginError"
                type="error"
                :closable="false"
                show-icon
            )
            el-button.login-btn(
                type="primary"
                size="medium"
                :loading="loggingIn"
                @click="login"
            ) {{ $t('Anmelden') }}

    //- App
    template(v-else)
        .scan-topbar
            .topbar-left
                router-link.back-btn(v-if="backRoute" :to="{ name: backRoute }")
                    i.el-icon-fa-chevron-left.fas
                .app-brand(v-else)
                    img.topbar-logo(:src="logo" alt="CityOffer")
                    span CityOffer App
            .topbar-center(v-if="isScanner && eventInfo.name")
                span.event-name {{ eventInfo.name }}
            .topbar-center(v-else-if="pageTitle")
                span.page-title {{ pageTitle }}
            .topbar-right
                el-button.switch-account-btn(
                    v-if="canSwitch"
                    type="text"
                    size="small"
                    :title="$t('Wechseln')"
                    @click="openSwitcher"
                )
                    i.el-icon-fa-users.fas
                el-button(
                    type="text"
                    size="small"
                    @click="logout"
                )
                    i.el-icon-fa-sign-out-alt.fas
        .scan-content
            router-view

        //- Wechsler: Webauftritt (CMS) und/oder Account (Super-Admin)
        .account-switcher(v-if="showSwitcher")
            .switcher-panel
                .switcher-header
                    span.switcher-title {{ $t('Wechseln') }}
                    button.switcher-close(@click="showSwitcher = false")
                        i.el-icon-fa-xmark.fas

                //- Webauftritt (CMS) innerhalb des aktuellen Accounts
                template(v-if="hasMultipleCmss")
                    .switcher-section-label {{ $t('Webauftritt') }}
                    .switcher-cmss
                        button.switcher-item(
                            v-for="cms in cmss"
                            :key="cms.id"
                            :class="{ current: String(cms.id) === String(activeCmsId) }"
                            :disabled="switching"
                            @click="switchCms(cms)"
                        )
                            span.switcher-company
                                span {{ cms.name }}

                //- Account-Wechsel (nur Super-Admin)
                template(v-if="canSwitchAccount")
                    .switcher-section-label {{ $t('Account') }}
                    .switcher-search
                        i.el-icon-search
                        input(
                            v-model="switcherSearch"
                            :placeholder="$t('Firma suchen…')"
                            @input="onSwitcherSearch"
                        )
                    button.switcher-back(
                        v-if="isImpersonating"
                        :disabled="switching"
                        @click="backToSuperAdmin"
                    )
                        i.el-icon-fa-arrow-left.fas
                        |  {{ $t('Zurück zu Super-Admin') }}
                    .switcher-list(v-loading="switcherLoading")
                        button.switcher-item(
                            v-for="acc in mimicableAccounts"
                            :key="acc.id"
                            :class="{ current: String(acc.id) === String(currentUserId) }"
                            :disabled="switching"
                            @click="switchToAccount(acc)"
                        )
                            span.switcher-company
                                i.el-icon-fa-asterisk.fas.master-star(v-if="isMaster(acc)" :title="$t('Master-Account')")
                                span {{ acc.company || accountName(acc) || '—' }}
                            span.switcher-sub {{ [accountName(acc), acc.email].filter(Boolean).join(' · ') }}
                        p.switcher-empty(v-if="!switcherLoading && !mimicableAccounts.length") {{ $t('Keine Accounts gefunden') }}
</template>

<i18n>
{
  "de": {
    "Anmelden": "Anmelden",
    "E-Mail": "E-Mail",
    "Passwort": "Passwort",
    "Abmelden": "Abmelden",
    "Anmeldung fehlgeschlagen": "Anmeldung fehlgeschlagen",
    "Event Scanner": "Events - QR-Code Scanner",
    "Produkte": "Produkte",
    "Wechseln": "Wechseln",
    "Webauftritt": "Webauftritt",
    "Account": "Account",
    "Account wechseln": "Account wechseln",
    "Firma suchen…": "Firma suchen…",
    "Zurück zu Super-Admin": "Zurück zu Super-Admin",
    "Keine Accounts gefunden": "Keine Accounts gefunden",
    "Master-Account": "Master-Account",
    "Diese App benötigt iOS 15.1 oder neuer. Bitte aktualisiere dein Gerät für die volle Funktion.": "Diese App benötigt iOS 15.1 oder neuer. Bitte aktualisiere dein Gerät für die volle Funktion."
  }
}
</i18n>

<script>
import _ from 'lodash'
import { useAuthStore, useSetupStore } from '@/stores'
import { adminClient as apolloClient } from '@/store/apollo'
import UserApi from '@/store/api/system/user'
import { canMimicUser } from '@/helpers/mimic'
import logo from '@/assets/CObyVA.svg'

export default {
    name: 'ScanLayout',
    data() {
        return {
            logo,
            credentials: { email: '', password: '' },
            loggingIn: false,
            loginError: null,
            eventInfo: { name: '', scanned: 0, total: 0 },
            productCount: null,
            iosUnsupported: false,
            // Account-/User-Wechsler
            showSwitcher: false,
            switcherSearch: '',
            switcherAccounts: [],
            switcherLoading: false,
            switching: false,
        }
    },
    computed: {
        isAuthenticated() { return useAuthStore().isAuthenticated },
        authStore() { return useAuthStore() },
        isSuperUser() { return this.authStore.isSuperUser },
        isImpersonating() { return this.authStore.isImpersonating },
        canSwitchAccount() { return this.authStore.mayMimic || this.isImpersonating },
        // The same rule LoginController::loginAs enforces, so the switcher can
        // never offer an account the server refuses - this list was the one
        // mimic surface still gated on the bare super_user flag.
        mimicableAccounts() { return this.switcherAccounts.filter((account) => canMimicUser(account)) },
        currentUserId() { return _.get(this.authStore, 'user.id') },
        setupStore() { return useSetupStore() },
        cmss() { return this.setupStore.cmss || [] },
        activeCmsId() { return _.get(this.setupStore, 'activeCms.id') },
        hasMultipleCmss() { return this.cmss.length > 1 },
        canSwitch() { return this.canSwitchAccount || this.hasMultipleCmss },
        backRoute() { return this.$route.meta?.backTo || null },
        isScanner() { return this.$route.name === 'scan-scanner' },
        pageTitle() {
            if (this.$route.name === 'scan-products') {
                return this.productCount != null
                    ? `${this.$t('Produkte')} (${this.productCount})`
                    : this.$t('Produkte')
            }
            const titles = {
                'app-home': '',
                'scan-events': this.$t('Event Scanner'),
                'scan-scanner': '',
            }
            return titles[this.$route.name] ?? ''
        },
    },
    watch: {
        '$route.query.login': 'applyLoginPrefill',
    },
    mounted() {
        this.applyLoginPrefill()
        this.iosUnsupported = this.detectIosUnsupported()
        // iOS Safari (15+) faerbt die Adress-/Statusleiste nach dem theme-color-Meta —
        // in der dunklen Scan-App passend auf Schwarz setzen (Topbar-Farbe).
        this.setThemeColor('#111111')
        this.$root.$on('scan:event', info => { this.eventInfo = info })
        this.$root.$on('scan:reset', () => { this.eventInfo = { name: '', scanned: 0, total: 0 } })
        this.$root.$on('scan:products', count => { this.productCount = count })
    },
    beforeDestroy() {
        this.restoreThemeColor()
        this.$root.$off('scan:event')
        this.$root.$off('scan:reset')
        this.$root.$off('scan:products')
    },
    methods: {
        // Der QR-Code der Anmeldeliste kann den Team-Zugang mitbringen
        // (…/app/event/745?login=name@example.com) — dann ist die E-Mail
        // vorbelegt und der Cursor steht direkt im Passwortfeld.
        applyLoginPrefill() {
            const email = _.trim(this.$route.query.login)
            if (!email || this.isAuthenticated || this.credentials.email) return

            this.credentials.email = email
            this.$nextTick(() => {
                const input = this.$refs.passwordInput
                if (input && input.focus) input.focus()
            })
        },
        async login() {
            if (!this.credentials.email || !this.credentials.password) return
            this.loggingIn = true
            this.loginError = null
            try {
                await useAuthStore().signin(this.credentials)
            } catch {
                this.loginError = this.$t('Anmeldung fehlgeschlagen')
            } finally {
                this.loggingIn = false
            }
        },
        logout() { useAuthStore().signout() },

        openSwitcher() {
            this.showSwitcher = true
            if (this.canSwitchAccount && !this.switcherAccounts.length) this.fetchSwitcherAccounts()
        },
        // Webauftritt (CMS) innerhalb des aktuellen Accounts wechseln – gleicher Ablauf
        // wie im Haupt-Admin (Auth.vue): Cache leeren, activeCms setzen, Setup neu laden.
        async switchCms(cms) {
            if (this.switching || !cms || String(cms.id) === String(this.activeCmsId)) return
            this.switching = true
            const setupStore = this.setupStore
            try {
                await apolloClient.clearStore()
                cms.reloadComponents = false
                setupStore.updateActiveCms(cms)
                setupStore.updateLoadedState(false)
                await setupStore.fetchSetup()
                setupStore.updateLoadedState(true)
                this.showSwitcher = false
                // Zur Event-Liste des neuen Webauftritts
                if (this.$route.name !== 'scan-events') this.$router.push({ name: 'scan-events' })
            } catch (e) {
                setupStore.updateLoadedState(true)
            } finally {
                this.switching = false
            }
        },
        onSwitcherSearch: _.debounce(function () {
            this.fetchSwitcherAccounts()
        }, 300),
        async fetchSwitcherAccounts() {
            this.switcherLoading = true
            const filters = {}
            const query = (this.switcherSearch || '').trim()
            if (query.length >= 2) {
                filters.company = { key: 'company', match: '', like: query, from: '', to: '', in: [] }
            }
            try {
                const data = await UserApi.fetchUsersPaginated({
                    scope: 'base',
                    type: 'account',
                    pageInfo: { currentPage: 1, perPage: 50 },
                    variables: { order: 'company', direction: 'asc' },
                    filters,
                })
                this.switcherAccounts = _.get(data, 'items', []) || []
            } catch (e) {
                this.switcherAccounts = []
            } finally {
                this.switcherLoading = false
            }
        },
        async switchToAccount(acc) {
            if (this.switching || !acc || String(acc.id) === String(this.currentUserId)) return
            this.switching = true
            try {
                // Nach dem Benutzerwechsel auf die Startseite der App wechseln
                await this.authStore.mimic(acc.id, { name: 'app-home' })
                this.showSwitcher = false
            } finally {
                this.switching = false
            }
        },
        async backToSuperAdmin() {
            if (this.switching) return
            this.switching = true
            try {
                await this.authStore.stopMimic({ name: 'app-home' })
                this.showSwitcher = false
            } finally {
                this.switching = false
            }
        },
        accountName(acc) {
            return [_.get(acc, 'first_name'), _.get(acc, 'last_name')].filter(Boolean).join(' ')
                || _.get(acc, 'name') || ''
        },
        // Master-Account = übergeordnetes Projekt (hat einen Owner-Administrator),
        // in der App/Account-Liste mit blauem * gekennzeichnet.
        isMaster(acc) {
            return !!(_.get(acc, 'administrators') || []).find(admin => admin && admin.owner)
        },
        // theme-color-Meta setzen (anlegen falls nicht vorhanden) und vorigen Wert merken.
        setThemeColor(color) {
            let meta = document.querySelector('meta[name="theme-color"]')
            if (!meta) {
                meta = document.createElement('meta')
                meta.setAttribute('name', 'theme-color')
                document.head.appendChild(meta)
                this._createdThemeMeta = true
            } else {
                this._prevThemeColor = meta.getAttribute('content')
            }
            meta.setAttribute('content', color)
        },
        // Beim Verlassen der Scan-App den vorigen Zustand wiederherstellen.
        restoreThemeColor() {
            const meta = document.querySelector('meta[name="theme-color"]')
            if (!meta) return
            if (this._createdThemeMeta) {
                meta.parentNode.removeChild(meta)
            } else if (this._prevThemeColor != null) {
                meta.setAttribute('content', this._prevThemeColor)
            }
        },
        // iOS-Version aus dem User-Agent ermitteln; true wenn < 15.1 (nicht unterstuetzt)
        detectIosUnsupported() {
            const ua = navigator.userAgent || ''
            const isIos = /iPad|iPhone|iPod/.test(ua)
                || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
            if (!isIos) return false
            const m = ua.match(/OS (\d+)_(\d+)/)
            if (!m) return false
            const major = parseInt(m[1], 10)
            const minor = parseInt(m[2], 10)
            return major < 15 || (major === 15 && minor < 1)
        },
    },
}
</script>

<style lang="sass">
body.scan
    background: #0d0d0d
    margin: 0
    padding: 0
    // verhindert Doppeltipp-Zoom (z.B. bei schnellem Tippen auf Buttons) auf iOS,
    // Pinch-Zoom bleibt moeglich
    touch-action: manipulation
    -webkit-text-size-adjust: 100%
    // unterbindet den iOS-Bounce/Overscroll, der wie ein Scrollbalken wirkt
    overscroll-behavior: none

    // iOS Safari zoomt beim Fokussieren eines Eingabefelds automatisch rein,
    // wenn dessen Schriftgroesse < 16px ist -> alle Felder auf 16px setzen
    input,
    textarea,
    select
        font-size: 16px !important
</style>

<style lang="sass" scoped>
.scan-app
    height: 100vh
    height: 100dvh
    display: flex
    flex-direction: column
    overflow: hidden
    overscroll-behavior: none
    background: #0d0d0d
    color: #fff

// ── iOS-Versionshinweis ────────────────────────────────
.ios-warning
    flex-shrink: 0
    display: flex
    align-items: center
    gap: 8px
    padding: 10px 16px
    background: #e6a23c
    color: #1a1a1a
    font-size: 13px
    line-height: 1.3
    i
        font-size: 18px
        flex-shrink: 0

// ── Login ──────────────────────────────────────────────
.scan-login
    flex: 1
    min-height: 0
    display: flex
    align-items: center
    justify-content: center
    padding: 24px
    overflow-y: auto

    .login-card
        width: 100%
        max-width: 380px
        background: #1a1a1a
        border: 1px solid #2a2a2a
        border-radius: 12px
        padding: 28px 24px

        .login-logo
            text-align: center
            margin-bottom: 24px
            img
                height: 44px
                width: auto
                display: block
                margin: 0 auto 10px
            h2
                margin: 0
                font-size: 20px
                font-weight: 600
                color: #d0d0d0

        .login-fields
            display: flex
            flex-direction: column
            gap: 12px
            margin-bottom: 16px

        ::v-deep
            .el-input__inner
                background: #111
                border-color: #333
                color: #fff
                &::placeholder
                    color: #555
                &:focus
                    border-color: $--color-primary
            .el-input__prefix
                color: #555
            .el-alert
                background: rgba(245,108,108,0.12)
                border-color: rgba(245,108,108,0.3)
                margin-bottom: 16px
                border-radius: 6px
                &__title
                    color: #f56c6c

        .login-btn
            width: 100%
            margin-top: 4px
            border-radius: 6px

// ── Topbar ─────────────────────────────────────────────
.scan-topbar
    display: flex
    align-items: center
    justify-content: space-between
    height: 56px
    flex-shrink: 0
    padding: 0 16px
    background: #111
    border-bottom: 1px solid #222
    z-index: 100

    .topbar-left
        display: flex
        align-items: center
        gap: 8px
        flex-shrink: 0

        .back-btn
            color: $--color-primary
            font-size: 18px
            text-decoration: none
            padding: 4px 6px

        .app-brand
            display: flex
            align-items: center
            gap: 8px
            .topbar-logo
                height: 24px
                width: auto
            span
                font-size: 15px
                font-weight: 600
                color: #d0d0d0

    .topbar-center
        position: absolute
        left: 50%
        transform: translateX(-50%)
        display: flex
        flex-direction: column
        align-items: center
        gap: 2px
        pointer-events: none

        .event-name
            font-size: 14px
            font-weight: 600
            color: #d0d0d0
            white-space: nowrap
            max-width: 180px
            overflow: hidden
            text-overflow: ellipsis

        .page-title
            font-size: 15px
            font-weight: 600
            color: #d0d0d0

    .topbar-right
        flex-shrink: 0
        display: flex
        align-items: center
        gap: 4px
        ::v-deep .el-button
            color: #888
            i
                margin-right: 0

// ── Account-Wechsler ───────────────────────────────────
.account-switcher
    position: fixed
    inset: 0
    z-index: 200
    background: rgba(0,0,0,0.55)
    display: flex
    align-items: flex-start
    justify-content: center

    .switcher-panel
        width: 100%
        max-width: 460px
        max-height: 100%
        display: flex
        flex-direction: column
        background: #141414
        border: 1px solid #2a2a2a
        overflow: hidden

    .switcher-header
        flex-shrink: 0
        display: flex
        align-items: center
        justify-content: space-between
        padding: 14px 16px
        border-bottom: 1px solid #222

        .switcher-title
            font-size: 16px
            font-weight: 600
            color: #eee

        .switcher-close
            background: none
            border: none
            color: #888
            font-size: 18px
            cursor: pointer
            padding: 4px 6px

    .switcher-section-label
        flex-shrink: 0
        padding: 12px 16px 6px
        font-size: 11px
        font-weight: 700
        text-transform: uppercase
        letter-spacing: 1px
        color: #6b6e73

    .switcher-cmss
        flex-shrink: 0
        display: flex
        flex-wrap: wrap
        gap: 8px
        padding: 2px 16px 14px
        max-height: 38vh
        overflow-y: auto
        border-bottom: 1px solid #1e1e1e

        .switcher-item
            width: auto
            flex: none
            display: inline-flex
            align-items: center
            padding: 8px 12px
            border: 1px solid #333
            border-radius: 4px
            background: #1a1a1a
            color: #c2c6cc
            cursor: pointer
            transition: border-color .15s, background .15s, color .15s

            &:disabled
                opacity: 0.6

            &.current
                background: rgba(64,158,255,0.15)
                border-color: $--color-primary
                color: $--color-primary

            .switcher-company
                display: inline
                font-size: 13px
                font-weight: 600
                color: inherit

    .switcher-search
        flex-shrink: 0
        display: flex
        align-items: center
        gap: 8px
        padding: 10px 16px
        border-bottom: 1px solid #1e1e1e

        i
            color: #666
            font-size: 14px

        input
            flex: 1
            min-width: 0
            background: #1a1a1a
            border: 1px solid #333
            border-radius: 4px
            color: #fff
            padding: 8px 10px
            outline: none
            &::placeholder
                color: #555
            &:focus
                border-color: $--color-primary

    .switcher-back
        flex-shrink: 0
        display: flex
        align-items: center
        gap: 8px
        width: 100%
        padding: 12px 16px
        background: none
        border: none
        border-bottom: 1px solid #1e1e1e
        color: $--color-primary
        font-size: 14px
        font-weight: 600
        cursor: pointer
        &:disabled
            opacity: 0.5

    .switcher-list
        flex: 1
        min-height: 120px
        overflow-y: auto

        .switcher-item
            display: flex
            flex-direction: column
            gap: 2px
            width: 100%
            text-align: left
            padding: 12px 16px
            background: none
            border: none
            border-bottom: 1px solid #1a1a1a
            cursor: pointer
            transition: background .15s

            &:active
                background: #1a1a1a

            &.current
                background: rgba(64,158,255,0.1)

            .switcher-company
                display: flex
                align-items: center
                gap: 6px
                font-size: 15px
                font-weight: 600
                color: #e6e8eb

                .master-star
                    color: $--color-primary
                    font-size: 11px
            .switcher-sub
                font-size: 12px
                color: #8a8d92

        .switcher-empty
            padding: 24px 16px
            text-align: center
            color: #777
            font-size: 14px

// ── Content ────────────────────────────────────────────
.scan-content
    flex: 1
    min-height: 0
    padding: 0
    background: #0d0d0d
    display: flex
    flex-direction: column
</style>
